import { FC, useEffect } from 'react';
import { useIntl } from 'react-intl';

import { RecordLinks, RecordSummary, SearchInstanceDto } from '@app/components';
import {
  useAvailabilityContext,
  useMappingsContext,
  useSearchContext,
} from '@app/contexts';
import {
  useAppLocale,
  useFeatureFlag,
  useHoldsAndCheckoutsPageConfig,
} from '@app/hooks';
import {
  getItemStatusData,
  getItemWithStatus,
  getPageStartNumber,
} from '@app/utils';
import { defaultRecordsPerPage, Feature } from '@app/constants';

interface PageRecordsProps {
  isSearchSuccessful: boolean;
}

export const PageRecords: FC<PageRecordsProps> = ({ isSearchSuccessful }) => {
  const intl = useIntl();
  const { locale } = useAppLocale();
  const { selectConfiguredOption: selectConfiguredHoldsAndCheckoutsOption } =
    useHoldsAndCheckoutsPageConfig();
  const {
    getAvailability,
    availability,
    ILLLinks = {},
    isAvailabilityLoading,
  } = useAvailabilityContext();
  const {
    state: { pageNumber, recordsPerPage = defaultRecordsPerPage },
    searchResults: { currentPage = [] },
    setInstanceIdScrollTo,
  } = useSearchContext();
  const { userItemStatuses } = useMappingsContext();
  const RTACForSearchFlag = useFeatureFlag({
    name: Feature.RTACForSearch,
    isActiveIfNotFound: true,
  });
  const startNumber = getPageStartNumber({ recordsPerPage, pageNumber });

  const getRecordsIds = (records: SearchInstanceDto[] = []) =>
    records.map(record => record.id);

  useEffect(() => {
    if (isSearchSuccessful && RTACForSearchFlag.isActive) {
      getAvailability(getRecordsIds(currentPage), pageNumber);
    }
  }, [
    getAvailability,
    isSearchSuccessful,
    currentPage,
    pageNumber,
    RTACForSearchFlag?.isActive,
  ]);

  const renderPage = () =>
    currentPage.map((instance, index) => {
      const itemWithStatus = getItemWithStatus(instance.id, userItemStatuses);

      return (
        <RecordSummary
          recordNumber={startNumber + index}
          testId="record-summary"
          key={instance.id}
          instance={instance}
          placement="results"
          itemStatusData={
            itemWithStatus &&
            getItemStatusData(
              itemWithStatus,
              intl,
              locale,
              selectConfiguredHoldsAndCheckoutsOption
            )
          }
          availability={availability[instance.id]?.summaryAvailability}
          ILLLinks={ILLLinks[instance?.id]}
          isAvailabilityLoading={isAvailabilityLoading}
          isRTACShown={RTACForSearchFlag.isActive}
          links={
            <RecordLinks
              id={instance.id}
              title={instance.title}
              onClick={() => {
                setInstanceIdScrollTo(instance.id);
              }}
            />
          }
        />
      );
    });

  return <>{renderPage()}</>;
};
